import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { adminApi, authApi } from '../api';

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('cs_admin_token'));
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const login = async ({ username, password }) => {
    const res = await authApi.adminLogin({ username, password });
    localStorage.setItem('cs_admin_token', res.token);
    setToken(res.token);
    return res;
  };

  const logout = () => {
    localStorage.removeItem('cs_admin_token');
    setToken(null);
  };

  const refreshAll = useCallback(async () => {
    if (!token) return;
    try {
      const [s, p, o, d, u, pm, st, su] = await Promise.all([
        adminApi.stats().catch(() => null),
        adminApi.listProducts().catch(() => []),
        adminApi.listOrders().catch(() => []),
        adminApi.listDeposits().catch(() => []),
        adminApi.listUsers().catch(() => []),
        adminApi.listPaymentMethods().catch(() => []),
        adminApi.getSettings().catch(() => null),
        adminApi.supportUnread().catch(() => ({ unread: 0 })),
      ]);
      setStats(s); setProducts(p); setOrders(o); setDeposits(d);
      setUsers(u); setPayments(pm); setSettings(st);
      setSupportUnread(su?.unread || 0);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    refreshAll();
    const iv = setInterval(refreshAll, 15000);
    return () => clearInterval(iv);
  }, [token, refreshAll]);

  return (
    <AdminContext.Provider value={{
      token, login, logout, loading,
      stats, products, orders, deposits, users, payments, settings, supportUnread,
      refreshAll,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
};
